<script setup lang="ts">
import DOMPurify from 'dompurify'
import localforage from 'localforage'

import { ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  /** 图标标识：i-solar:xxx / i-carbon:xxx / URL / SVG / emoji */
  icon: string
  /** 图标尺寸，默认 1em */
  size?: string
}>(), {
  size: '1em',
})

// ===== 内存缓存 =====
const svgMemoryCache = new Map<string, string>()

// ===== localforage 持久化缓存（7天过期） =====
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000 // 7天

const iconCacheStore = localforage.createInstance({
  name: 'plugin-icon-cache',
  storeName: 'svg-cache',
})

interface CachedIcon {
  svg: string
  timestamp: number
}

async function getCachedSvg(key: string): Promise<string | null> {
  try {
    const cached = await iconCacheStore.getItem<CachedIcon>(key)
    if (cached) {
      if (Date.now() - cached.timestamp < CACHE_TTL)
        return cached.svg
      iconCacheStore.removeItem(key).catch(() => {}) // 异步清理过期条目
    }
    return null
  }
  catch {
    return null
  }
}

async function setCachedSvg(key: string, svg: string): Promise<void> {
  try {
    await iconCacheStore.setItem<CachedIcon>(key, { svg, timestamp: Date.now() })
  }
  catch {
    // 缓存写入失败不影响功能
  }
}

const supportedIconPrefixes = new Set(['solar', 'carbon', 'lucide', 'mingcute', 'simple-icons', 'eos-icons'])

/**
 * 解析 i-{prefix}:{name} 格式
 */
function parseIconClass(icon: string): { prefix: string, name: string } | null {
  const match = icon.match(/^i-([\w-]+):([\w-]+)$/)
  if (!match)
    return null
  return { prefix: match[1], name: match[2] }
}

/**
 * 按需取单个图标，避免把整个 Iconify 图标集下载到客户端。
 */
async function loadIconSvg(prefix: string, name: string): Promise<string | null> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 5000)

  try {
    const url = `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(name)}.svg`
    const response = await fetch(url, { signal: controller.signal })
    if (!response.ok)
      return null

    const svg = await response.text()
    if (!svg.trimStart().startsWith('<svg'))
      return null

    return DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true } })
  }
  finally {
    window.clearTimeout(timeout)
  }
}

function isSupportedIcon(icon: string): { prefix: string, name: string } | null {
  const parsed = parseIconClass(icon)
  if (!parsed || !supportedIconPrefixes.has(parsed.prefix))
    return null
  return parsed
}

// 渲染状态
const svgHtml = ref('')
const iconType = ref<'iconify' | 'url' | 'svg' | 'text'>('text')

watch(() => props.icon, async (icon) => {
  const currentIcon = icon

  if (!icon) {
    iconType.value = 'text'
    svgHtml.value = ''
    return
  }

  // URL 格式
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    iconType.value = 'url'
    return
  }

  // 内联 SVG（用 DOMPurify 消毒，防止 XSS）
  if (icon.trimStart().startsWith('<svg')) {
    iconType.value = 'svg'
    svgHtml.value = DOMPurify.sanitize(icon, { USE_PROFILES: { svg: true } })
    return
  }

  // i-{prefix}:{name} 格式
  const parsed = isSupportedIcon(icon)
  if (parsed) {
    iconType.value = 'iconify'

    // 1. 内存缓存命中 → 立即渲染
    const memCached = svgMemoryCache.get(icon)
    if (memCached) {
      svgHtml.value = memCached
      return
    }

    // 2. localforage 持久化缓存命中 → 快速渲染（无需加载图标集）
    const dbCached = await getCachedSvg(icon)
    if (props.icon !== currentIcon)
      return // 竞态检查
    if (dbCached) {
      svgMemoryCache.set(icon, dbCached)
      svgHtml.value = dbCached
      return
    }

    // 3. 都没命中 → 加载单个 SVG
    try {
      const html = await loadIconSvg(parsed.prefix, parsed.name)
      if (props.icon !== currentIcon)
        return // 竞态检查
      if (!html) {
        iconType.value = 'text'
        return
      }
      // 写入两级缓存
      svgMemoryCache.set(icon, html)
      setCachedSvg(icon, html)
      svgHtml.value = html
    }
    catch {
      iconType.value = 'text'
    }
    return
  }

  // fallback: emoji / 文字
  iconType.value = 'text'
}, { immediate: true })
</script>

<template>
  <!-- Iconify 单图标 SVG（已消毒） -->
  <span v-if="iconType === 'iconify' && svgHtml" class="plugin-icon" :style="{ fontSize: size }" v-html="svgHtml" />
  <!-- 图片 URL -->
  <img v-else-if="iconType === 'url'" :src="icon" :style="{ width: size, height: size }" alt="plugin icon" @error="iconType = 'text'">
  <!-- 内联 SVG（已 DOMPurify 消毒） -->
  <span v-else-if="iconType === 'svg'" class="plugin-icon" :style="{ width: size, height: size }" v-html="svgHtml" />
  <!-- emoji / 文字 fallback -->
  <span v-else :style="{ fontSize: size, lineHeight: 1 }">{{ icon || '🧩' }}</span>
</template>

<style scoped>
.plugin-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
